/**
 * Pure helper for the schedule-confirmation modal's duration validation.
 *
 * Extracted from CalendarView so the duration-challenge feature flag behaviour
 * is unit-testable and shared between the status banner and the footer button.
 */
export interface DurationStatusInput {
  /** Estimated duration for the job, in hours. */
  durationHours: number;
  /** Selected slot start. */
  start: Date;
  /** Selected slot end. */
  end: Date;
  /**
   * Whether the duration challenge is enabled for the current client. When
   * disabled, exceeding the estimate is never flagged — the job is scheduled
   * regardless of the selected duration.
   */
  durationChallengeEnabled: boolean;
}

export interface DurationStatus {
  /** Selected duration in hours, rounded to 1dp. */
  diffHours: number;
  /** End is at or before start. */
  isBeforeStart: boolean;
  /** End is before the minimum (estimated) duration. */
  isBelowDuration: boolean;
  /** Selected duration exceeds the estimate AND the challenge is enabled. */
  isAboveDuration: boolean;
  /** All checks pass — safe to schedule directly. */
  isValid: boolean;
}

export function computeDurationStatus({
  durationHours,
  start,
  end,
  durationChallengeEnabled,
}: DurationStatusInput): DurationStatus {
  const minEndDt = new Date(start.getTime() + durationHours * 60 * 60 * 1000);
  const diffMs = end.getTime() - start.getTime();
  const diffHours = Math.round((diffMs / (1000 * 60 * 60)) * 10) / 10;

  const isBeforeStart = diffMs <= 0;
  const isBelowDuration = end < minEndDt;
  const isAboveDuration = durationChallengeEnabled && !isBeforeStart && diffHours > durationHours;
  const isValid = !isBeforeStart && !isBelowDuration && !isAboveDuration;

  return { diffHours, isBeforeStart, isBelowDuration, isAboveDuration, isValid };
}
